import { useEffect, useRef } from "react";

/**
 * Scrolls the window back to the top whenever `key` changes.
 *
 * Used for in-page step changes (choosing a feeling, opening an exercise
 * introduction, starting a practice) so people always land at the title and
 * instructions of the new step instead of part-way down the previous one.
 * The very first render is left alone — global route scrolling handles that.
 */
export function useScrollTopOnChange(
  key: string | null,
  targetRef?: { current: HTMLElement | null },
) {
  const previous = useRef<string | null>(key);

  useEffect(() => {
    if (previous.current === key) return;
    previous.current = key;
    if (typeof window === "undefined") return;
    let top = 0;
    const el = targetRef?.current;
    if (el) {
      // Align the section start just below the sticky header. Near the page
      // top this resolves to 0, so standalone pages still start at the top.
      const header = document.querySelector("header");
      const offset = (header?.getBoundingClientRect().height ?? 0) + 8;
      top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - offset);
      if (top < 200) top = 0;
    }
    try {
      window.scrollTo({ top, left: 0, behavior: "instant" as ScrollBehavior });
    } catch {
      window.scrollTo(0, top);
    }
  }, [key]);
}
